import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, AlertCircle } from '../../icons/wp-icons';
import { fahrzeugApi } from '../../api/fahrzeugApi';

const STATUS_LABELS = {
  VERFUEGBAR: 'Verfügbar',
  WARTUNG: 'In Wartung',
  AUSFALL: 'Ausgefallen',
  AUSSORTIERT: 'Aussortiert'
};

const STATUS_COLORS = {
  VERFUEGBAR: 'bg-green-100 text-green-800',
  WARTUNG: 'bg-amber-100 text-amber-800',
  AUSFALL: 'bg-red-100 text-red-800',
  AUSSORTIERT: 'bg-gray-100 text-gray-600'
};

function formatDate(value) {
  if (!value) return '–';
  return new Date(value).toLocaleDateString('de-DE');
}

// Tage bis zum Datum — negativ wenn überfällig
function daysUntil(value) {
  if (!value) return null;
  const ms = new Date(value).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function PflichtterminBadge({ label, datum }) {
  if (!datum) return null;
  const days = daysUntil(datum);
  if (days === null) return null;
  let cls = 'bg-gray-100 text-gray-600';
  if (days < 0) cls = 'bg-red-100 text-red-800';
  else if (days <= 30) cls = 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${cls}`}>
      {days < 0 && <AlertCircle className="w-3 h-3" />}
      {label}: {formatDate(datum)}
    </span>
  );
}

function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function FahrzeugModal({ fahrzeug, onClose, onSaved }) {
  const isEdit = !!fahrzeug;
  const [form, setForm] = useState({
    kennzeichen: fahrzeug?.kennzeichen || '',
    bezeichnung: fahrzeug?.bezeichnung || '',
    typ: fahrzeug?.typ || '',
    marke: fahrzeug?.marke || '',
    modell: fahrzeug?.modell || '',
    status: fahrzeug?.status || 'VERFUEGBAR',
    tuevBis: toDateInput(fahrzeug?.tuevBis),
    versicherungBis: toDateInput(fahrzeug?.versicherungBis),
    naechsteWartung: toDateInput(fahrzeug?.naechsteWartung),
    kapazitaetQm: fahrzeug?.kapazitaetQm ?? '',
    maxGewichtKg: fahrzeug?.maxGewichtKg ?? '',
    bemerkung: fahrzeug?.bemerkung || ''
  });
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.kennzeichen.trim() || !form.bezeichnung.trim()) {
      toast.error('Kennzeichen und Bezeichnung sind Pflicht');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        kapazitaetQm: form.kapazitaetQm === '' ? null : Number(form.kapazitaetQm),
        maxGewichtKg: form.maxGewichtKg === '' ? null : parseInt(form.maxGewichtKg, 10),
        tuevBis: form.tuevBis || null,
        versicherungBis: form.versicherungBis || null,
        naechsteWartung: form.naechsteWartung || null
      };
      if (isEdit) {
        await fahrzeugApi.update(fahrzeug.id, payload);
        toast.success('Fahrzeug aktualisiert');
      } else {
        await fahrzeugApi.create(payload);
        toast.success('Fahrzeug angelegt');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">{isEdit ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kennzeichen *</label>
              <input value={form.kennzeichen} onChange={setField('kennzeichen')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="B-MD-2023" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bezeichnung *</label>
              <input value={form.bezeichnung} onChange={setField('bezeichnung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Sprinter Sanitär 1" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
              <input value={form.typ} onChange={setField('typ')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Transporter, Pkw, Anhänger" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={setField('status')} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marke</label>
              <input value={form.marke} onChange={setField('marke')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Mercedes" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modell</label>
              <input value={form.modell} onChange={setField('modell')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Sprinter 316" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TÜV bis</label>
              <input type="date" value={form.tuevBis} onChange={setField('tuevBis')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Versicherung bis</label>
              <input type="date" value={form.versicherungBis} onChange={setField('versicherungBis')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nächste Wartung</label>
              <input type="date" value={form.naechsteWartung} onChange={setField('naechsteWartung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max. Gewicht (kg)</label>
              <input type="number" value={form.maxGewichtKg} onChange={setField('maxGewichtKg')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Laderaum (m³)</label>
              <input type="number" step="0.1" value={form.kapazitaetQm} onChange={setField('kapazitaetQm')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bemerkung</label>
              <textarea value={form.bemerkung} onChange={setField('bemerkung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Abbrechen</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              {saving ? 'Speichere…' : (isEdit ? 'Speichern' : 'Anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function FahrzeugeTab() {
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editFahrzeug, setEditFahrzeug] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await fahrzeugApi.getAll(filterStatus ? { status: filterStatus } : {});
      setFahrzeuge(data);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const handleDelete = async (id) => {
    try {
      await fahrzeugApi.delete(id);
      toast.success('Fahrzeug gelöscht');
      setDeleteConfirmId(null);
      await load();
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Alle Status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => { setEditFahrzeug(null); setShowModal(true); }} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark">
          <Plus className="w-5 h-5" /> Neues Fahrzeug
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Lade…</div>
        ) : fahrzeuge.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Noch keine Fahrzeuge angelegt.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Kennzeichen</th>
                <th className="px-4 py-3">Bezeichnung</th>
                <th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Pflichttermine</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fahrzeuge.map(f => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-medium">{f.kennzeichen}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{f.bezeichnung}</div>
                    {(f.marke || f.modell) && <div className="text-xs text-gray-500">{[f.marke, f.modell].filter(Boolean).join(' ')}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{f.typ || '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[f.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABELS[f.status] || f.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <PflichtterminBadge label="TÜV" datum={f.tuevBis} />
                      <PflichtterminBadge label="Vers." datum={f.versicherungBis} />
                      <PflichtterminBadge label="Wartung" datum={f.naechsteWartung} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditFahrzeug(f); setShowModal(true); }} className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirmId(f.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <FahrzeugModal fahrzeug={editFahrzeug} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Fahrzeug löschen?</h3>
            <p className="text-sm text-gray-600 mb-4">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirmId(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Abbrechen</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
